/**
 * Announcement bar (<announcement-bar>, sections/announcement.liquid).
 *
 * Accessibility contract (ADA demand letter 2026-09, Phase 0):
 * - Rotation runs only while shouldPlay() is true. That single predicate combines the
 *   user's pause/play choice with transient interaction pauses (hover, keyboard focus,
 *   touch), the bar being off-screen, and the document being hidden. A user pause is
 *   never overridden by anything except the user pressing Play.
 * - prefers-reduced-motion: no autoplay on load (the button starts in its Play state).
 * - Inactive slides are inert (fallback: aria-hidden + tabindex=-1 on their focusables)
 *   and kept in sync with the visible slide, including after a manual swipe.
 * - The slide container is aria-live="off" while rotating and "polite" once paused.
 * - Every timer and global listener is released in disconnectedCallback.
 *
 * Expiry guard (Phase 1): a scheduled slide carries data-ends-at (Unix seconds, from
 * snippets/announcement-schedule.liquid). Liquid already drops ended offers, but a
 * CDN- or browser-cached page can still hold one, so before the slides are cached the
 * guard removes every slide whose end has passed (same >= boundary as Liquid and the
 * countdown), renumbers "N of M", drops the carousel roles and the pause button when
 * one slide is left, hides the bar when none are, and re-measures the header stack.
 * It re-runs at the next end time, when a countdown in the bar dispatches
 * `countdown:end`, and when the tab becomes visible again.
 *
 * Dismissible blocks (Phase 2): a slide with data-dismiss-key has a close button
 * (.js-announcement-dismiss) after its content. Pressing it stores the key in
 * sessionStorage ("1"; a storage error only means it is dismissed for this page) and
 * removes the slide through the same path as an expiry, so renumbering, the collapse
 * at one slide, the hide at zero, the header re-measure and the focus hand-off
 * (never to <body>) are identical. On load a slide dismissed earlier this session
 * (already hidden pre-paint by the inline script in sections/announcement.liquid)
 * is removed before the carousel caches its slides. The click is tracked as
 * dismiss_announcement by assets/analytics-tracking.js, whose capture listener runs
 * before the slide is removed.
 */

/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]';
  const SUPPORTS_INERT = typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;

  /**
   * Whether the visitor asked the OS to minimise motion.
   * @returns {MediaQueryList|null}
   */
  function reducedMotionQuery() {
    return typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
  }

  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.js-announcement-slides');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');

      // "Paused by user" is kept apart from "paused by interaction" so that no
      // interaction ending (mouseleave, focusout, tab return) can resume a bar the
      // user stopped with the button.
      this.userPaused = false;
      this.hovered = false;
      this.focused = false;
      this.editorSelected = false;
      this.timer = null;
      this.expiryTimer = null;
      this.currentIndex = 0;
    }

    connectedCallback() {
      if (this.connectedOnce) return;
      this.connectedOnce = true;
      this.bindEvents();
      this.applyExpiry();
      this.init();
    }

    disconnectedCallback() {
      this.stopTimer();
      this.clearExpiryTimer();

      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }

      if (this.motionQuery && this.motionHandler) {
        if (typeof this.motionQuery.removeEventListener === 'function') {
          this.motionQuery.removeEventListener('change', this.motionHandler);
        } else if (typeof this.motionQuery.removeListener === 'function') {
          this.motionQuery.removeListener(this.motionHandler);
        }
      }

      // Relocated copies in the mobile menu belong to this instance; a re-rendered
      // bar (Theme Editor, section reload) builds its own.
      document.querySelectorAll('.mob__announcement-links, .mob__localization')
        .forEach((el) => el.remove());

      this.connectedOnce = false;
    }

    /**
     * Theme Editor: show the selected block's slide and hold rotation there.
     * @param {HTMLElement} slide - The selected block's slide element.
     */
    editorSelect(slide) {
      if (!this.slides) return;
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.editorSelected = true;
      this.goToSlide(index);
      this.restartTimer();
    }

    /**
     * Theme Editor: release the hold; the user's own pause/play state is untouched.
     */
    editorDeselect() {
      if (!this.editorSelected) return;
      this.editorSelected = false;
      this.restartTimer();
    }

    bindEvents() {
      if (this.links.length) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      // Timers are throttled in background tabs, so a returning visitor gets a
      // fresh expiry check before anything else.
      this.visibilityHandler = this.visibilityHandler || (() => {
        if (!document.hidden) this.applyExpiry();
        this.restartTimer();
      });
      document.addEventListener('visibilitychange', this.visibilityHandler);

      if (!this.countdownEndBound) {
        this.countdownEndBound = true;
        this.addEventListener('countdown:end', () => this.applyExpiry());
      }

      if (!this.dismissBound) {
        this.dismissBound = true;
        this.addEventListener('click', (evt) => {
          const node = evt.target;
          const target = node && node.nodeType === 1 ? node : node && node.parentElement;
          const button = target && target.closest ? target.closest('.js-announcement-dismiss') : null;
          if (!button || !this.contains(button)) return;
          this.dismissSlide(button.closest('.announcement__text'));
        });
      }

      if (!this.slider) return;

      this.motionQuery = reducedMotionQuery();
      if (this.motionQuery) {
        this.motionHandler = this.motionHandler || ((evt) => {
          // Turning reduced motion ON stops rotation. Turning it OFF never resumes:
          // only the user's Play does.
          if (evt.matches) this.setUserPaused(true);
        });
        if (typeof this.motionQuery.addEventListener === 'function') {
          this.motionQuery.addEventListener('change', this.motionHandler);
        } else if (typeof this.motionQuery.addListener === 'function') {
          this.motionQuery.addListener(this.motionHandler);
        }
      }

      // Listeners on the bar's own children survive a disconnect/reconnect, so they
      // are bound exactly once.
      if (this.localEventsBound) return;
      this.localEventsBound = true;

      this.autoplayButton = this.querySelector('.js-announcement-autoplay');
      this.autoplayLabel = this.querySelector('.js-announcement-autoplay-label');

      if (this.autoplayButton) {
        this.autoplayButton.addEventListener('click', () => this.toggleUserPause());
      }

      // Interaction pauses are scoped to the slides, not the button: pressing Play
      // focuses (and, with a mouse, hovers) the button, and that must not cancel it.
      // The element is captured here: collapseCarousel() nulls this.slider, and a
      // late event must never dereference it.
      const slider = this.slider;
      slider.addEventListener('mouseenter', () => this.setInteraction('hovered', true));
      slider.addEventListener('mouseleave', () => this.setInteraction('hovered', false));
      slider.addEventListener('focusin', () => this.setInteraction('focused', true));
      slider.addEventListener('focusout', (evt) => {
        if (!slider.contains(evt.relatedTarget)) this.setInteraction('focused', false);
      });
      // A touch is a deliberate "let me read this": it is treated as a user pause,
      // so the button visibly flips to Play and only Play resumes rotation.
      slider.addEventListener('touchstart', () => {
        if (!this.userPaused) this.setUserPaused(true);
      }, { passive: true });

      this.scrollHandler = debounce(() => this.syncToScrollPosition(), 150);
      slider.addEventListener('scroll', this.scrollHandler, { passive: true });

    }

    init() {
      if (this.slider) {
        this.slides = Array.from(this.slider.querySelectorAll('.announcement__text'));
        this.setActiveSlide(this.currentIndex < this.slides.length ? this.currentIndex : 0);
        // On a reconnect, a pause the user chose must survive.
        this.setUserPaused(this.userPaused || Boolean(this.motionQuery && this.motionQuery.matches));
      }

      if (this.links.length) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Whether a slide's end (data-ends-at, Unix seconds) has been reached.
     * @param {HTMLElement} slide - Slide element.
     * @param {number} now - Current time in ms.
     * @returns {boolean}
     */
    static isExpired(slide, now) {
      const endsAt = Number(slide.dataset.endsAt);
      return Number.isFinite(endsAt) && endsAt > 0 && now >= endsAt * 1000;
    }

    /**
     * Whether a slide was dismissed: marked by the pre-paint script or a click
     * (data-dismissed), or stored for this session under its data-dismiss-key.
     * @param {HTMLElement} slide - Slide element.
     * @returns {boolean}
     */
    static isDismissed(slide) {
      if (slide.hasAttribute('data-dismissed')) return true;
      const key = slide.dataset.dismissKey;
      if (!key) return false;
      try {
        return window.sessionStorage.getItem(key) === '1';
      } catch (e) {
        return false;
      }
    }

    /**
     * Dismisses one slide: remembers it for the session (best effort) and removes it
     * through the expiry path.
     * @param {HTMLElement|null} slide - The slide holding the pressed close button.
     */
    dismissSlide(slide) {
      if (!slide || !this.contains(slide)) return;
      const key = slide.dataset.dismissKey;
      // The Theme Editor never persists a dismissal: the merchant would lose the
      // block's preview for the rest of the session.
      if (key && !this.hasAttribute('data-design-mode')) {
        try {
          window.sessionStorage.setItem(key, '1');
        } catch (e) {
          /* Storage blocked or full: dismissed for this page view only. */
        }
      }
      slide.setAttribute('data-dismissed', '');
      // Hand focus on whenever it was in the slide OR nowhere (on <body>): iOS
      // VoiceOver's double-tap and a Safari mouse click activate the button without
      // focusing it, and leaving focus on <body> drops the VoiceOver cursor to the
      // top of the page. preventScroll keeps the page still; a mouse user gets no
      // visible ring (no :focus-visible after a pointer interaction).
      const active = document.activeElement;
      const hadFocus = !active || active === document.body || slide.contains(active);
      slide.remove();
      this.reflowSlides(hadFocus);
      this.scheduleExpiry(Date.now());
    }

    /**
     * Removes ended and dismissed slides, then reflows what is left and schedules
     * the next check.
     * @returns {boolean} True when a slide was removed.
     */
    applyExpiry() {
      // Theme Editor previews scheduled blocks outside their window on purpose.
      if (this.hasAttribute('data-design-mode')) return false;
      const now = Date.now();
      const slides = Array.from(this.querySelectorAll('.announcement__text'));
      const expired = slides.filter((slide) => AnnouncementBar.isExpired(slide, now)
        || AnnouncementBar.isDismissed(slide));

      if (expired.length) {
        const active = document.activeElement;
        const hadFocus = Boolean(active && expired.some((slide) => slide.contains(active)));
        expired.forEach((slide) => slide.remove());
        this.reflowSlides(hadFocus);
      }

      this.scheduleExpiry(now);
      return expired.length > 0;
    }

    clearExpiryTimer() {
      if (this.expiryTimer !== null) {
        clearTimeout(this.expiryTimer);
        this.expiryTimer = null;
      }
    }

    /**
     * Arms one timer for the soonest remaining end time.
     * @param {number} now - Current time in ms.
     */
    scheduleExpiry(now) {
      this.clearExpiryTimer();
      const ends = Array.from(this.querySelectorAll('.announcement__text[data-ends-at]'))
        .map((slide) => Number(slide.dataset.endsAt) * 1000)
        .filter((ms) => Number.isFinite(ms) && ms > now);
      if (!ends.length) return;
      // setTimeout overflows above 2^31-1 ms (~24.8 days); re-check then instead.
      const wait = Math.min(Math.min(...ends) - now, 2147483647);
      this.expiryTimer = setTimeout(() => {
        this.expiryTimer = null;
        this.applyExpiry();
      }, wait);
    }

    /**
     * Brings labels, controls and tracking positions in line with the slides that
     * are left after an expiry.
     * @param {boolean} restoreFocus - Focus was inside a removed slide.
     */
    reflowSlides(restoreFocus) {
      const slides = Array.from(this.querySelectorAll('.announcement__text'));
      const total = slides.length;

      slides.forEach((slide, i) => {
        slide.querySelectorAll('[data-track-position]').forEach((el) => {
          el.setAttribute('data-track-position', String(i + 1));
        });
      });

      if (total === 0) {
        this.stopTimer();
        this.slides = [];
        const left = this.querySelector('.announcement__col--left');
        const right = this.querySelector('.announcement__col--right');
        // Keep the bar only if the right column (links, selectors) is actually
        // rendered; on mobile it is display:none and the bar would be an empty strip.
        if (right && right.getClientRects().length > 0) {
          if (left) left.hidden = true;
        } else {
          (this.closest('.shopify-section') || this).hidden = true;
        }
      } else if (total === 1) {
        this.collapseCarousel(slides[0]);
      } else if (this.slider) {
        const template = this.slider.dataset.slideLabel;
        if (template) {
          slides.forEach((slide, i) => {
            slide.setAttribute(
              'aria-label',
              template.replace('[index]', String(i + 1)).replace('[total]', String(total))
            );
          });
        }
        this.slides = slides;
        const index = Math.min(this.currentIndex, total - 1);
        this.goToSlide(index);
        this.restartTimer();
      }

      if (restoreFocus) this.restoreFocusAfterExpiry(slides);

      if (typeof window.setHeaderHeight === 'function') window.setHeaderHeight();
    }

    /**
     * Focus sat in a slide that was just removed. Hand it to the slide now shown
     * (its first focusable, else the bar itself), or, when the bar is gone, to the
     * start of the main content (else the header logo). Never left on <body>.
     * @param {HTMLElement[]} slides - Remaining slides.
     */
    restoreFocusAfterExpiry(slides) {
      const focusOn = (el) => {
        if (!el) return false;
        if (!el.matches(FOCUSABLE) || el.getAttribute('tabindex') === '-1') {
          if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        }
        el.focus({ preventScroll: true });
        return document.activeElement === el;
      };

      const section = this.closest('.shopify-section') || this;
      if (slides.length && !section.hidden) {
        const target = slides[Math.min(this.currentIndex, slides.length - 1)];
        const focusable = target.querySelector(FOCUSABLE);
        if (focusable && focusOn(focusable)) return;
        if (focusOn(this)) return;
      }

      if (focusOn(document.getElementById('main-content'))) return;
      focusOn(document.querySelector('.logo__link'));
    }

    /**
     * One slide left: it becomes a plain message, with no carousel roles, no
     * pause button and no rotation (the same markup Liquid renders for one block).
     * @param {HTMLElement} slide - The remaining slide.
     */
    collapseCarousel(slide) {
      this.stopTimer();
      ['role', 'aria-roledescription', 'aria-label'].forEach((attr) => slide.removeAttribute(attr));
      AnnouncementBar.setSlideInert(slide, false);
      slide.classList.add('is-visible');

      const carousel = this.querySelector('.announcement__carousel');
      if (carousel) {
        ['role', 'aria-roledescription', 'aria-label'].forEach((attr) => carousel.removeAttribute(attr));
      }
      const button = this.querySelector('.js-announcement-autoplay');
      if (button) button.remove();
      this.autoplayButton = null;
      this.autoplayLabel = null;

      if (this.slider) {
        // Stop syncing scroll position first: the reset scroll below would
        // otherwise fire it after this.slider is gone.
        if (this.scrollHandler) this.slider.removeEventListener('scroll', this.scrollHandler);
        this.slider.removeAttribute('aria-live');
        if (typeof this.slider.scrollTo === 'function') this.slider.scrollTo({ left: 0, top: 0 });
      }
      // Without a slider init() leaves the single slide alone and shouldPlay()
      // stays false.
      this.slider = null;
      this.slides = [slide];
      this.currentIndex = 0;
    }

    /**
     * The single autoplay predicate. Every input that can stop rotation feeds it.
     * @returns {boolean}
     */
    shouldPlay() {
      if (!this.slides || this.slides.length < 2) return false;
      if (this.userPaused) return false;
      if (this.hovered || this.focused || this.editorSelected) return false;
      if (document.hidden) return false;
      if (window.theme && window.theme.elementUtil && typeof window.theme.elementUtil.isInViewport === 'function') {
        return window.theme.elementUtil.isInViewport(this);
      }
      return true;
    }

    get slideDelay() {
      const delay = Number(this.dataset.slideDelay);
      return Number.isFinite(delay) && delay > 0 ? delay : 7000;
    }

    stopTimer() {
      if (this.timer !== null) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    /**
     * Restarts the full read-time countdown. Called on every state change so a
     * slide never advances moments after a pause ends.
     */
    restartTimer() {
      this.stopTimer();
      if (!this.slides || this.slides.length < 2 || this.userPaused) return;

      this.timer = setTimeout(() => {
        this.timer = null;
        if (this.shouldPlay()) this.goToSlide(this.currentIndex + 1);
        this.restartTimer();
      }, this.slideDelay);
    }

    /**
     * @param {'hovered'|'focused'} key - Interaction flag.
     * @param {boolean} value - Whether the interaction is active.
     */
    setInteraction(key, value) {
      if (this[key] === value) return;
      this[key] = value;
      this.restartTimer();
    }

    toggleUserPause() {
      this.setUserPaused(!this.userPaused);
    }

    /**
     * Records the user's (or reduced-motion's) rotation choice and reflects it in
     * the button label and the live-region politeness.
     * @param {boolean} paused - True to stop rotation.
     */
    setUserPaused(paused) {
      this.userPaused = paused;

      if (this.slider) {
        this.slider.setAttribute('aria-live', paused ? 'polite' : 'off');
      }

      if (this.autoplayButton) {
        this.autoplayButton.classList.toggle('is-paused', paused);
        const label = paused
          ? this.autoplayButton.dataset.labelPlay
          : this.autoplayButton.dataset.labelPause;
        if (label && this.autoplayLabel) this.autoplayLabel.textContent = label;
      }

      this.restartTimer();
    }

    /**
     * Scrolls to a slide and makes it the only one reachable by assistive tech.
     * @param {number} index - Target slide index; wraps around.
     */
    goToSlide(index) {
      if (!this.slides || !this.slides.length) return;
      const count = this.slides.length;
      const next = ((index % count) + count) % count;
      // Collapsed to a plain message (no slider): nothing to scroll.
      if (!this.slider) {
        this.setActiveSlide(next);
        return;
      }
      const width = this.slider.clientWidth || this.slides[next].clientWidth;
      const left = document.dir === 'rtl' ? -Math.abs(next * width) : next * width;

      if (typeof this.slider.scrollTo === 'function') {
        const reduce = this.motionQuery && this.motionQuery.matches;
        this.slider.scrollTo({ left, top: 0, behavior: reduce ? 'auto' : 'smooth' });
      }

      this.setActiveSlide(next);
    }

    /**
     * Re-derives the visible slide after a manual swipe/scroll, so the slide on
     * screen is never the inert one.
     */
    syncToScrollPosition() {
      if (!this.slides || !this.slider || !this.slider.clientWidth) return;
      const index = Math.round(Math.abs(this.slider.scrollLeft) / this.slider.clientWidth);
      if (index !== this.currentIndex && index < this.slides.length) {
        this.setActiveSlide(index);
      }
    }

    /**
     * @param {number} index - Slide to expose; every other slide becomes inert.
     */
    setActiveSlide(index) {
      // If focus sits in a slide that is about to become inert (a swipe while a
      // link is focused), hand it to the newly visible slide first so it is never
      // stranded on an inert, off-screen element.
      const active = document.activeElement;
      const target = this.slides[index];
      if (active && target && !target.contains(active)
        && this.slides.some((slide) => slide !== target && slide.contains(active))) {
        const focusable = target.querySelector(FOCUSABLE);
        if (focusable) {
          focusable.focus({ preventScroll: true });
        } else {
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      }

      this.currentIndex = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-visible', active);
        AnnouncementBar.setSlideInert(slide, !active);
      });
    }

    /**
     * Removes a slide (and everything in it) from the tab order and the
     * accessibility tree, with an aria-hidden/tabindex fallback where `inert` is
     * unsupported.
     * @param {HTMLElement} slide - Slide element.
     * @param {boolean} inert - True to hide it.
     */
    static setSlideInert(slide, inert) {
      if (inert) {
        slide.setAttribute('inert', '');
      } else {
        slide.removeAttribute('inert');
      }

      if (SUPPORTS_INERT) return;

      if (inert) {
        slide.setAttribute('aria-hidden', 'true');
      } else {
        slide.removeAttribute('aria-hidden');
      }

      slide.querySelectorAll(FOCUSABLE).forEach((el) => {
        if (inert) {
          if (!el.hasAttribute('data-announcement-tabindex')) {
            el.setAttribute('data-announcement-tabindex', el.getAttribute('tabindex') ?? '');
          }
          el.setAttribute('tabindex', '-1');
        } else if (el.hasAttribute('data-announcement-tabindex')) {
          const original = el.getAttribute('data-announcement-tabindex');
          if (original === '') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', original);
          }
          el.removeAttribute('data-announcement-tabindex');
        }
      });
    }

    /**
     * Move the help links to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      if (!this.menu || !window.theme || !window.theme.mediaMatches) return;
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!window.theme.mediaMatches.md && !menuAnnouncementLinks) {
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNavUl.classList.add('secondary-nav');

        this.links.forEach((link) => {
          const label = link.textContent.trim();
          if (!label) return;
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = 'secondary-nav__item';
          a.href = link.getAttribute('href');
          a.textContent = label;
          li.appendChild(a);
          mobNavUl.appendChild(li);
        });

        if (!mobNavUl.children.length) return;

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (window.theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.localization = this.querySelector('.announcement__localization');
      this.menu = document.querySelector('.main-menu__content');
      if (!this.localization || !this.menu) return;
      const menuLocalization = document.querySelector('.mob__localization');
      if (!window.theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (window.theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
